import type { Attendance, ScheduledBook, Session, User, Venue } from "@book-club/shared";
import { orderBy, where } from "firebase/firestore";
import type { AttendanceWithUser, SessionWithDetails } from "@/features/sessions/types";
import { getMonthDateRange } from "@/features/sessions/utils/dateUtils";
import firebaseDatabaseService from "@/infrastructure/firebase/firebaseDatabaseService";
import logger from "@/logger";

export type AttendanceStatusCounts = {
    absent: number;
    attended: number;
    cancelled: number;
    unconfirmed: number;
};

/**
 * Fetches all sessions for the given month
 *
 * Query flow:
 * 1. Builds date range for the target month
 * 2. Queries sessions collection where scheduledAt is within the month range
 * 3. Returns sessions with IDs only (no resolved data)
 *
 * @param year - Full year (e.g., 2026)
 * @param month - Month number 1-12 (January = 1, December = 12)
 * @returns Array of sessions
 */
async function getSessionsForMonth(year: number, month: number): Promise<Session[]> {
    logger.debug(`[Sessions] Fetching sessions for month ${month}/${year}`);

    const { start, end } = getMonthDateRange(year, month);

    logger.debug("[Sessions] Querying Firestore 'sessions' collection...");

    const sessions = await firebaseDatabaseService.getDocuments<Session>(
        "sessions",
        where("scheduledAt", ">=", start.toISOString()),
        where("scheduledAt", "<=", end.toISOString())
    );

    logger.debug(`[Sessions] Firestore returned ${sessions.length} session documents`);

    if (sessions.length === 0) {
        logger.debug("[Sessions] No sessions found in Firestore for this month");
        return [];
    }

    logger.debug(`[Sessions] Returning ${sessions.length} sessions for ${month}/${year}`);

    return sessions;
}

async function getSessionsForBook(bookId: string): Promise<Session[]> {
    logger.debug(`[Sessions] Fetching sessions for book ${bookId}`);
    const sessions = await firebaseDatabaseService.getDocuments<Session>(
        "sessions",
        where("bookId", "==", bookId),
        orderBy("scheduledAt", "asc")
    );
    logger.debug(`[Sessions] Firestore returned ${sessions.length} session documents for book ${bookId}`);
    return sessions;
}

/**
 * Fetches a single scheduled book by ID from Firestore
 *
 * @param bookId - Book document ID to fetch
 * @returns ScheduledBook object or null if not found
 */
async function fetchScheduledBook(bookId: string): Promise<ScheduledBook | null> {
    logger.debug(`[Sessions] Fetching scheduled book: ${bookId}`);

    const book = await firebaseDatabaseService.getDocument<ScheduledBook>("books", bookId);

    if (!book) {
        logger.warn(`[Sessions] Book not found: ${bookId}`);
        return null;
    }

    if (book.type === "specificBook") {
        logger.debug(`[Sessions] Found scheduled book: "${book.title}" by ${book.author}`);
    }
    if (book.type === "authorFocus") {
        logger.debug(`[Sessions] Found scheduled book: Author Focus by ${book.author}`);
    }
    if (book.type === "freeChoice") {
        logger.debug(`[Sessions] Found scheduled book: Free Choice`);
    }
    return book;
}

/**
 * Fetches a single venue by ID from Firestore
 *
 * @param venueId - Venue document ID to fetch
 * @returns Venue object or null if not found
 */
async function fetchVenue(venueId: string): Promise<Venue | null> {
    logger.debug(`[Sessions] Fetching venue: ${venueId}`);

    const venue = await firebaseDatabaseService.getDocument<Venue>("venues", venueId);

    if (!venue) {
        logger.warn(`[Sessions] Venue not found: ${venueId}`);
        return null;
    }

    logger.debug(`[Sessions] Found venue: "${venue.name}"`);
    return venue;
}

/**
 * Fetches a single session by ID with resolved book and venue details
 *
 * @param sessionId - Session document ID to fetch
 * @returns SessionWithDetails object with book and venue resolved, or throws if session not found
 */
async function getSessionById(sessionId: string): Promise<SessionWithDetails> {
    logger.debug(`[Sessions] Fetching session: ${sessionId}`);

    const session = await firebaseDatabaseService.getDocument<Session>("sessions", sessionId);

    if (!session) {
        logger.warn(`[Sessions] Session not found: ${sessionId}`);
        throw new Error(`Session not found: ${sessionId}`);
    }

    // Fetch book and venue in parallel
    const [book, venue] = await Promise.all([
        session.bookId ? fetchScheduledBook(session.bookId) : Promise.resolve(null),
        session.venueId ? fetchVenue(session.venueId) : Promise.resolve(null),
    ]);

    const result: SessionWithDetails = {
        ...session,
        book,
        venue,
    };

    logger.debug(`[Sessions] Found session: ${sessionId} with book and venue`);
    return result;
}

/**
 * Fetches all attendance records for a session with resolved user details
 *
 * @param sessionId - Session document ID to fetch attendance for
 * @returns Array of AttendanceWithUser objects
 */
async function getAttendanceForSession(sessionId: string): Promise<AttendanceWithUser[]> {
    logger.debug(`[Sessions] Fetching attendance for session: ${sessionId}`);

    const attendanceRecords = await firebaseDatabaseService.getDocuments<Attendance>(
        "attendance",
        where("sessionId", "==", sessionId)
    );

    logger.debug(`[Sessions] Found ${attendanceRecords.length} attendance records for session ${sessionId}`);

    if (attendanceRecords.length === 0) {
        return [];
    }

    // Fetch user details for each attendance record in parallel
    const attendanceWithUsers = await Promise.all(
        attendanceRecords.map(async (attendance) => {
            const user = await firebaseDatabaseService.getDocument<User>("users", attendance.uid);
            return {
                ...attendance,
                user: user ?? null,
            } as AttendanceWithUser;
        })
    );

    logger.debug(`[Sessions] Returning ${attendanceWithUsers.length} attendance records with user details`);

    return attendanceWithUsers;
}

/**
 * Active booking / attendance status counts for a session via Firestore count() aggregations.
 * Matches getAttendanceStatus priority: absent → attended → cancelled → unconfirmed.
 * Does not download attendance or user documents.
 */
async function getAttendanceStatusCounts(sessionId: string): Promise<AttendanceStatusCounts> {
    logger.debug(`[Sessions] Counting attendance statuses for session: ${sessionId}`);

    const [absent, attended, cancelled, unconfirmed] = await Promise.all([
        firebaseDatabaseService.getCount(
            "attendance",
            where("sessionId", "==", sessionId),
            where("wasAbsent", "==", true)
        ),
        firebaseDatabaseService.getCount(
            "attendance",
            where("sessionId", "==", sessionId),
            where("wasAbsent", "==", false),
            where("attendedAt", "!=", null),
            orderBy("attendedAt")
        ),
        firebaseDatabaseService.getCount(
            "attendance",
            where("sessionId", "==", sessionId),
            where("wasAbsent", "==", false),
            where("attendedAt", "==", null),
            where("cancelledAt", "!=", null),
            orderBy("cancelledAt")
        ),
        firebaseDatabaseService.getCount(
            "attendance",
            where("sessionId", "==", sessionId),
            where("wasAbsent", "==", false),
            where("attendedAt", "==", null),
            where("cancelledAt", "==", null)
        ),
    ]);

    return { absent, attended, cancelled, unconfirmed };
}

const sessionsService = {
    fetchScheduledBook,
    fetchVenue,
    getAttendanceForSession,
    getAttendanceStatusCounts,
    getSessionById,
    getSessionsForBook,
    getSessionsForMonth,
};

export default sessionsService;
